import {
  Box,
  InputAdornment,
  OutlinedInput,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Loader } from "./Loader";
import emptyIcon from "../../assets/images/Icons/search-table.png";
import CloseIcon from "@mui/icons-material/Close";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";
import { SortableTableHeader } from "./SortableTableHeader";

export const ServerPaginatedtable = (props) => {
  const [headerHover, setHeaderHover] = React.useState(null);
  const {
    defaultData,
    columns,
    headerText,
    rightContent,
    subRow = null,
    customRowStyle = null,
    tableHeaderContent = null,
    isHeaderStyleChange = false,
    isPagination = true,
    isTableHeader = true,
    hideColumnHeader = false,
    tableFooter,
    search,
    pagination,
    onSearchChange,
    setPagination,
    paginationProps,
    isLoading = false,
    allRowsExpanded = false,
    manualSortBy = true,
    sorting,
    setSorting,
    updateTableData,
    onRowLink = false,
    onRowAction = false,
    rowClickNewTab = false,
    emptyMessage = "No data found",
    isSearchActive = false,
    isInTab = false,
    headerContentStyle = {},
    headerStyle = {},
    enableTableContainerPaddingBottom = false,
    onChangeRowSelection = null,
    resetRowSelection = false,
    defaultColumn = undefined,
    editRow = null,
    getEditRow = null,
  } = props;

  const reactTableParams = {
    data: defaultData,
    columns,
    defaultColumn,
    manualPagination: true,
    manualSorting: manualSortBy,
    onGlobalFilterChange: onSearchChange,
    onPaginationChange: setPagination,
    getSubRows: (row) => row[subRow] || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      globalFilter: search,
      pagination,
    },
    meta: {
      updateData: updateTableData,
      editRow,
      getEditRow,
    },
  };

  /**
   * Sets up sorting parameters for the React table if manual sorting is enabled.
   *
   * @param {boolean} manualSortBy - Indicates if manual sorting is allowed.
   * @param {function} setSorting - Function to update the sorting state.
   * @param {Array} sorting - Current sorting configuration.
   */
  if (manualSortBy && setSorting && sorting) {
    reactTableParams.onSortingChange = setSorting;
    reactTableParams.state.sorting = sorting;
  }

  /**
   * Initializes the React table with the provided parameters.
   *
   * @type {ReturnType<typeof useReactTable>}
   */
  const table = useReactTable(reactTableParams);

  /**
   * Handles mouse hover status over table headers.
   *
   * @param {boolean} status - The hover status to set.
   */
  const handleMouseHover = (status) => setHeaderHover(status);

  React.useEffect(() => {
    if (table && allRowsExpanded) {
      table.toggleAllRowsExpanded(true);
    }
  }, [table, allRowsExpanded]);

  // ================================================================= ROW SELECTION ==================================================================
  const currentRowSelection = table.getState()?.rowSelection;

  // Reset row selection to default
  React.useEffect(() => {
    if (resetRowSelection)
      table.setState((prevState) => ({
        ...prevState,
        rowSelection: {},
      }));
  }, [resetRowSelection]);

  // Find the row details and set to callback
  React.useEffect(() => {
    if (typeof onChangeRowSelection === "function") {
      const selected = currentRowSelection || {};
      const selectedIds = Object.keys(selected).filter((id) => selected[id]);
      const selectedRows = selectedIds
        .map((id) => {
          try {
            const row = table.getRow(id);
            return row ? row.original : null;
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
      onChangeRowSelection(selectedRows);
    }
  }, [currentRowSelection]);

  return (
    <>
      <Stack
        sx={{
          borderRadius: "4px",
          bgcolor: "#fff",
          // overflow: "hidden",
          // mb: "8px",
        }}
      >
        {isTableHeader && (
          <TableHeader
            setGlobalFilter={onSearchChange}
            headerText={headerText}
            rightContent={rightContent}
            isHeaderStyleChange={isHeaderStyleChange}
            search={search}
            isInTab={isInTab}
            headerStyle={headerStyle}
          />
        )}

        {tableHeaderContent && (
          <TableHeaderContent
            tableHeaderContent={tableHeaderContent}
            headerContentStyle={headerContentStyle}
          />
        )}

        <TableContainer
          // component={Paper}
          sx={{
            overflow: "auto",
            backgroundColor: "#F5F8FC",
            pb: enableTableContainerPaddingBottom ? "8px" : 0,
          }}
        >
          <Table
            sx={{
              mx: isInTab ? "0" : "8px",
              width: isInTab ? "100%" : "calc(100% - 16px)",
              mt: hideColumnHeader ? "8px" : "0px",
            }}
          >
            {!hideColumnHeader && (
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const columnMetaData = header.column.columnDef.meta;
                        if (!header?.column?.columnDef?.hidden) {
                          const canSort = header.column.getCanSort();
                          const headerName = flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          );
                          const sort = header.column.getIsSorted();
                          const isSortActive = sort !== false;

                          return (
                            <TableCell
                              key={header.id}
                              colSpan={header.colSpan}
                              align={
                                header?.column?.columnDef?.align === "right"
                                  ? "right"
                                  : "left"
                              }
                              onMouseEnter={
                                canSort
                                  ? () => handleMouseHover(header.id)
                                  : undefined
                              }
                              onMouseLeave={
                                canSort
                                  ? () => handleMouseHover(null)
                                  : undefined
                              }
                              sx={{ ...columnMetaData }}
                            >
                              {header.isPlaceholder ? null : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    justifyContent:
                                      header?.column?.columnDef?.align ===
                                        "right" && "flex-end",
                                    cursor: canSort ? "pointer" : "",
                                    fontWeight: 600,
                                    fontSize: "12px",
                                  }}
                                  {...{
                                    onClick:
                                      header.column.getToggleSortingHandler(),
                                  }}
                                >
                                  {canSort ? (
                                    <SortableTableHeader
                                      headerHover={headerHover}
                                      headerId={header.id}
                                      headerName={headerName}
                                      isSortActive={isSortActive}
                                      sort={sort}
                                    />
                                  ) : (
                                    headerName
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          );
                        }
                      })}
                    </TableRow>
                  );
                })}
              </TableHead>
            )}
            <TableBody
              sx={{
                border: isInTab ? 0 : "1px solid #DFE8F2",
                // backgroundColor: "blue",

                // "& tr:first-of-type td": {
                //   borderTopLeftRadius: 8,
                //   borderTopRightRadius: 8,
                // },
                // "& tr:last-of-type td": {
                //   borderBottomLeftRadius: 8,
                //   borderBottomRightRadius: 8,
                // },
                // "& td": {
                //   // backgroundColor: "#fff",
                // },
              }}
            >
              {isLoading || table.getFilteredRowModel().rows.length === 0 ? (
                isLoading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : (
                  <EmptyComponent
                    isSearchActive={isSearchActive}
                    emptyMessage={emptyMessage}
                    search={search}
                  />
                )
              ) : (
                table.getRowModel().rows.map((row) => {
                  const style = customRowStyle ? customRowStyle(row) : {};
                  const isCustomRow =
                    row.depth > 0 && row.original.isCustomRow ? true : false;
                  const link =
                    onRowLink && (subRow ? row.depth > 0 : true)
                      ? onRowLink(row.original)
                      : false;
                  return (
                    <TableRow
                      key={row.id}
                      component={link ? Link : null}
                      to={link ? link : undefined}
                      onClick={() => {
                        onRowAction && onRowAction(row.original);
                      }}
                      target={rowClickNewTab ? "_blank	" : ""}
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: "none",
                        cursor: link || onRowAction ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            link || onRowAction
                              ? "rgba(55, 65, 81, 0.04)"
                              : false,
                        },
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const cellMetaData = cell.column.columnDef.meta;

                        if (!cell?.column?.columnDef?.hidden) {
                          return isCustomRow ? (
                            <TableCell
                              key={cell.id}
                              sx={{
                                // borderBottom: "1px solid #DFE8F2",
                                ...style,
                                ...cellMetaData,
                              }}
                              align={
                                cell.column.id === "actions" ? "right" : "left"
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ) : (
                            <TableCell
                              key={cell.id}
                              sx={{
                                ...style,
                                ...cellMetaData,
                              }}
                              align={
                                cell.column.id === "actions" ? "right" : "left"
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          );
                        }
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {tableFooter && <>{tableFooter}</>}
          </Table>

          {isPagination && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              colSpan={0}
              count={paginationProps.total}
              rowsPerPage={pagination?.pageSize}
              page={pagination?.pageIndex > 0 ? pagination?.pageIndex - 1 : 0}
              onPageChange={(e) => table.setPageIndex(e)}
              onRowsPerPageChange={(e) => {
                table.setPageSize(Number(e.target.value));
                table.setPageIndex(1);
              }}
              ActionsComponent={(subProps) => (
                <TablePaginationActions
                  {...subProps}
                  lastPage={paginationProps.lastPage}
                />
              )}
              sx={{
                bgcolor: "#FFFFFF",
                borderInline: isInTab ? "0" : "1px solid #DFE8F2",
                mx: isInTab ? "0" : "8px",
              }}
            />
          )}
        </TableContainer>
      </Stack>
    </>
  );
};

export function TablePaginationActions(props) {
  const { onPageChange, lastPage, page } = props;
  return (
    <Box sx={{ flexShrink: 0, marginLeft: "20px", height: "auto" }}>
      <Pagination
        count={lastPage}
        page={page + 1}
        shape="rounded"
        showFirstButton
        showLastButton
        onChange={(e, page) => onPageChange(page)}
      />
    </Box>
  );
}

const TableHeader = ({
  setGlobalFilter,
  headerText,
  rightContent,
  isHeaderStyleChange,
  search,
  isInTab,
  headerStyle,
}) => {
  const { fontWeight, fontSize } = headerStyle;
  const titleFontWeight = isInTab || fontWeight ? 500 : 600;
  const titleFontSize = isInTab || fontSize ? "18px" : "24px";
  return (
    <Stack
      sx={{
        borderRadius: "4px",
        padding: "16px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: isHeaderStyleChange && "#111C2B",
        color: isHeaderStyleChange && "#fff",
        position: "sticky",
        top: "0px",
        background: "#fff",
        zIndex: 99,
        "&::stuck": {
          backgroundColor: "#000",
        },
        height: "62px",
        ...headerStyle,
      }}
    >
      <Typography
        sx={{
          fontWeight: titleFontWeight,
          fontSize: titleFontSize,
        }}
      >
        {headerText}
      </Typography>
      {setGlobalFilter && (
        <OutlinedInput
          onChange={(e) => setGlobalFilter(e.target.value)}
          sx={{
            height: "36px",
            borderRadius: "10px",
            width: "200px",
            backgroundColor: isHeaderStyleChange ? "#313C4C" : "#F2F7FC",
            borderColor: isHeaderStyleChange && "#485364",
            "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: isHeaderStyleChange && "#485364",
            },
            "& input::placeholder": {
              color: isHeaderStyleChange && "#ccc",
            },

            "& input": {
              p: " 12.5px 14px 12.5px 4px !important",
            },
            "&:focus-within": {
              width: "400px",
            },
            transition: "width 0.5s",
          }}
          value={search}
          placeholder="Search"
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon size="small" />
            </InputAdornment>
          }
          endAdornment={
            search && (
              <InputAdornment
                sx={{ cursor: "pointer" }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent default behavior
                  setGlobalFilter("");
                }}
                position="end"
              >
                <CloseIcon size="large" />
              </InputAdornment>
            )
          }
        />
      )}

      {rightContent}
    </Stack>
  );
};

const EmptyComponent = ({ isSearchActive, emptyMessage, search }) => {
  return (
    <TableRow>
      <TableCell
        align="center"
        colSpan={20}
        height={162}
        sx={{ py: 2, padding: "32px 0" }}
      >
        <Stack alignItems="center" gap="8px">
          <img src={emptyIcon} alt="empty" height={56} width={56} />
          <Typography
            sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
          >
            {isSearchActive || search ? "No matches found" : emptyMessage}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const TableHeaderContent = ({
  tableHeaderContent,
  headerContentStyle = {},
}) => {
  return (
    <Box
      sx={{
        p: "8px 16px",
        borderBottom: "1px solid #DFE8F2",
        ...headerContentStyle,
      }}
    >
      {tableHeaderContent}
    </Box>
  );
};
